import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import {
  Waves, Lightning, Check, CreditCard, ArrowLeft,
  CurrencyBtc, CurrencyEth, ArrowRight, Envelope, WarningCircle,
  Sun, Moon, List, X
} from '@phosphor-icons/react';
import { Logo, LogoText } from '../components/Logo';

const plans = [
  { id: 'starter', name: 'Starter', price: 20, credits: 1000, pricePerUrl: '0.020', popular: false, features: ['Instant indexing', 'Bulk URL submission', 'Status tracking', 'API Access'] },
  { id: 'growth', name: 'Growth', price: 90, credits: 5000, pricePerUrl: '0.018', popular: true, features: ['Instant indexing', 'Bulk URL submission', 'Status tracking', 'API Access'] },
  { id: 'pro', name: 'Pro', price: 225, credits: 15000, pricePerUrl: '0.015', popular: false, features: ['Instant indexing', 'Bulk URL submission', 'Status tracking', 'API Access'] },
  { id: 'business', name: 'Business', price: 650, credits: 50000, pricePerUrl: '0.013', popular: false, features: ['Instant indexing', 'Bulk URL submission', 'Status tracking', 'API Access'] },
  { id: 'enterprise', name: 'Enterprise', price: 1050, credits: 100000, pricePerUrl: '0.0105', popular: false, features: ['Instant indexing', 'Bulk URL submission', 'Status tracking', 'API Access'] }
];

const cryptoCurrencies = [
  { id: 'btc', name: 'Bitcoin', symbol: 'BTC' },
  { id: 'eth', name: 'Ethereum', symbol: 'ETH' },
  { id: 'bnbbsc', name: 'BNB BSC', symbol: 'BNB' },
  { id: 'sol', name: 'Solana', symbol: 'SOL' },
  { id: 'usdtbsc', name: 'USDT BEP20', symbol: 'USDT' },
  { id: 'usdttrc20', name: 'USDT TRC20', symbol: 'USDT' },
  { id: 'ltc', name: 'Litecoin', symbol: 'LTC' },
  { id: 'doge', name: 'Dogecoin', symbol: 'DOGE' },
  { id: 'trx', name: 'Tron', symbol: 'TRX' },
  { id: 'xrp', name: 'Ripple', symbol: 'XRP' },
  { id: 'matic', name: 'Polygon', symbol: 'MATIC' }
];

export default function Pricing() {
  const { user } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleSelectPlan = (plan) => {
    if (!user) { navigate('/register'); return; }
    navigate(`/payment?plan=${plan.id}`);
  };

  return (
    <div className="min-h-screen" style={{ background: 'var(--bg-main)', color: 'var(--text-primary)' }}>
      {/* Navigation */}
      <nav className="fixed top-0 w-full z-50 backdrop-blur-xl border-b" style={{ background: 'var(--backdrop-blur)', borderColor: 'var(--border-main)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 sm:gap-3" data-testid="pricing-logo">
            <Logo size="md" />
            <LogoText size="md" />
          </Link>

          {/* Desktop nav */}
          <div className="hidden md:flex items-center gap-4 lg:gap-6">
            <Link to="/about" className="text-sm" style={{ color: 'var(--text-secondary)' }} data-testid="nav-about">About</Link>
            <Link to="/faq" className="text-sm" style={{ color: 'var(--text-secondary)' }} data-testid="nav-faq">FAQ</Link>
            <Link to="/pricing" className="text-sm font-medium" style={{ color: 'var(--accent)' }} data-testid="nav-pricing">Pricing</Link>
            <Link to="/docs" className="text-sm" style={{ color: 'var(--text-secondary)' }} data-testid="nav-docs">API Docs</Link>
            <Link to="/support" className="text-sm" style={{ color: 'var(--text-secondary)' }} data-testid="nav-support">Support</Link>
            {user ? (
              <>
                <Link to="/dashboard" className="text-sm" style={{ color: 'var(--text-secondary)' }} data-testid="nav-dashboard">Dashboard</Link>
                <div className="flex items-center gap-2 border px-3 py-1.5 rounded-lg text-sm" style={{ background: 'var(--bg-card)', borderColor: 'var(--border-accent)' }}>
                  <CreditCard weight="duotone" className="w-4 h-4" style={{ color: 'var(--accent)' }} />
                  <span className="font-bold">{user.credits || 0}</span>
                  <span className="text-xs hidden sm:inline" style={{ color: 'var(--text-muted)' }}>credits</span>
                </div>
              </>
            ) : (
              <>
                <Link to="/login" className="text-sm" style={{ color: 'var(--text-secondary)' }} data-testid="nav-login">Login</Link>
                <Link to="/register" className="btn-primary text-sm" data-testid="nav-register">Get Started</Link>
              </>
            )}
            <button onClick={toggleTheme} className="p-2 rounded-lg transition-colors" style={{ color: 'var(--text-secondary)' }} data-testid="pricing-theme-toggle">
              {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </button>
          </div>

          {/* Mobile nav */}
          <div className="flex md:hidden items-center gap-2">
            <button onClick={toggleTheme} className="p-2 rounded-lg" style={{ color: 'var(--text-secondary)' }}>
              {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </button>
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="p-2 rounded-lg">
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <List className="w-6 h-6" />}
            </button>
          </div>
        </div>
        {mobileMenuOpen && (
          <div className="md:hidden border-t px-4 py-4 space-y-3" style={{ background: 'var(--bg-card)', borderColor: 'var(--border-main)' }}>
            <Link to="/about" onClick={() => setMobileMenuOpen(false)} className="block py-2 text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>About</Link>
            <Link to="/faq" onClick={() => setMobileMenuOpen(false)} className="block py-2 text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>FAQ</Link>
            <Link to="/docs" onClick={() => setMobileMenuOpen(false)} className="block py-2 text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>API Docs</Link>
            <Link to="/support" onClick={() => setMobileMenuOpen(false)} className="block py-2 text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>Support</Link>
            {user ? (
              <Link to="/dashboard" onClick={() => setMobileMenuOpen(false)} className="block py-2 text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>Dashboard</Link>
            ) : (
              <>
                <Link to="/login" onClick={() => setMobileMenuOpen(false)} className="block py-2 text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>Login</Link>
                <Link to="/register" onClick={() => setMobileMenuOpen(false)} className="btn-primary text-sm text-center block">Get Started</Link>
              </>
            )}
          </div>
        )}
      </nav>

      {/* Header */}
      <section className="pt-24 sm:pt-32 pb-10 sm:pb-16 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto text-center">
          <Link to="/" className="inline-flex items-center gap-2 text-sm transition-colors mb-6" style={{ color: 'var(--text-secondary)' }}>
            <ArrowLeft className="w-4 h-4" /> Back to home
          </Link>
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6" style={{ background: 'var(--accent-bg)', border: '1px solid var(--border-accent)' }}>
            <CreditCard weight="fill" className="w-4 h-4" style={{ color: 'var(--accent)' }} />
            <span className="text-xs tracking-wider uppercase font-semibold" style={{ color: 'var(--accent)' }}>Pricing</span>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight mb-4">
            Simple, Transparent <span style={{ color: 'var(--accent)' }}>Pricing</span>
          </h1>
          <p className="text-sm sm:text-lg max-w-2xl mx-auto" style={{ color: 'var(--text-secondary)' }}>
            Pay with cryptocurrency. Get instant URL credits. No subscriptions, no hidden fees.
          </p>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="pb-10 sm:pb-16 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 sm:gap-5">
            {plans.map((plan) => (
              <div
                key={plan.id}
                className="relative border rounded-xl p-5 sm:p-6 transition-all duration-300 hover:-translate-y-1"
                style={{
                  background: 'var(--bg-card)',
                  borderColor: plan.popular ? 'var(--accent)' : 'var(--border-main)',
                  boxShadow: 'var(--shadow-card)'
                }}
                data-testid={`pricing-card-${plan.id}`}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full text-xs font-bold" style={{ background: 'linear-gradient(135deg, var(--accent-green), #059669)', color: '#ffffff' }}>
                    BEST VALUE
                  </div>
                )}
                {plan.limited && (
                  <div className="absolute -top-3 right-4 bg-gradient-to-r from-amber-500 to-orange-500 px-3 py-1 rounded-full text-xs font-bold text-white flex items-center gap-1 animate-pulse" data-testid="limited-badge">
                    <Lightning weight="fill" className="w-3 h-3" /> LIMITED
                  </div>
                )}

                <h3 className="text-lg font-bold mb-1">{plan.name}</h3>
                <div className="mb-4"><span className="text-3xl sm:text-4xl font-black">${plan.price}</span></div>
                <div className="mb-6 pb-6 border-b" style={{ borderColor: 'var(--border-main)' }}>
                  <p className="font-bold text-xl sm:text-2xl" style={{ color: 'var(--accent)' }}>{plan.credits.toLocaleString()}</p>
                  <p className="text-sm" style={{ color: 'var(--text-muted)' }}>URL Credits</p>
                </div>
                <ul className="space-y-3 mb-6">
                  <li className="flex items-center gap-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
                    <Check weight="bold" className="w-4 h-4 text-emerald-400" />
                    ${plan.pricePerUrl} per URL
                  </li>
                  {plan.features.map((feature, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
                      <Check weight="bold" className="w-4 h-4 text-emerald-400" />
                      {feature}
                      {feature === 'API Access' && (
                        <span className="ml-auto text-xs px-2 py-0.5 rounded font-medium" style={{ background: 'var(--accent-bg)', color: 'var(--accent)' }}>Included</span>
                      )}
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => handleSelectPlan(plan)}
                  className={`w-full py-3 font-bold rounded-lg transition-all flex items-center justify-center gap-2 ${plan.popular ? 'btn-primary' : ''}`}
                  style={!plan.popular ? { background: 'var(--accent-bg)', color: 'var(--accent)', border: '1px solid var(--border-accent)' } : {}}
                  data-testid={`buy-${plan.id}`}
                >
                  Select Plan <ArrowRight weight="bold" className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Accepted Crypto */}
      <section className="pb-10 sm:pb-16 px-4 sm:px-6">
        <div className="max-w-4xl mx-auto">
          <h3 className="text-center text-lg font-bold mb-6" style={{ color: 'var(--text-secondary)' }}>Accepted Cryptocurrencies</h3>
          <div className="flex flex-wrap justify-center gap-2 sm:gap-3">
            {cryptoCurrencies.map((crypto) => (
              <div key={crypto.id} className="flex items-center gap-2 border px-3 sm:px-4 py-2 rounded-lg transition-colors" style={{ background: 'var(--bg-card)', borderColor: 'var(--border-main)' }} data-testid={`crypto-badge-${crypto.id}`}>
                {crypto.id === 'btc' && <CurrencyBtc weight="duotone" className="w-5 h-5 text-amber-500" />}
                {crypto.id === 'eth' && <CurrencyEth weight="duotone" className="w-5 h-5 text-purple-400" />}
                {!['btc', 'eth'].includes(crypto.id) && (
                  <div className="w-5 h-5 rounded-full flex items-center justify-center" style={{ background: 'var(--accent-bg)' }}>
                    <span className="text-[8px] font-bold" style={{ color: 'var(--accent)' }}>{crypto.symbol.charAt(0)}</span>
                  </div>
                )}
                <span className="font-medium text-xs sm:text-sm">{crypto.id === 'usdtbsc' ? 'USDT BEP20' : crypto.id === 'usdttrc20' ? 'USDT TRC20' : crypto.symbol}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Fees Notice */}
      <section className="pb-10 sm:pb-16 px-4 sm:px-6">
        <div className="max-w-2xl mx-auto">
          <div className="bg-amber-500/5 border border-amber-500/20 rounded-xl p-4 sm:p-5">
            <div className="flex gap-3">
              <WarningCircle weight="fill" className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-bold text-sm text-amber-400 mb-2">Important Note</h3>
                <ul className="text-xs space-y-1.5" style={{ color: 'var(--text-secondary)' }}>
                  <li>Please send <strong className="text-amber-300">$0.05 - $0.10 extra</strong> than the displayed amount for smooth automatic transaction completion.</li>
                  <li>USDT TRC20 transactions include a <strong className="text-amber-300">$5 extra gas fee</strong>.</li>
                  <li>A <strong className="text-amber-300">1% service fee</strong> applies to all transactions.</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Need Help */}
      <section className="pb-16 sm:pb-24 px-4 sm:px-6">
        <div className="max-w-2xl mx-auto">
          <div className="border rounded-xl p-6 sm:p-8 text-center" style={{ background: 'var(--bg-card)', borderColor: 'var(--border-main)', boxShadow: 'var(--shadow-card)' }}>
            <h3 className="text-xl font-bold mb-2">Need Help Choosing?</h3>
            <p className="text-sm mb-6" style={{ color: 'var(--text-secondary)' }}>Visit our support center for guidance on picking the right plan</p>
            <Link to="/support" className="btn-primary inline-flex items-center gap-2" data-testid="support-contact">
              <Envelope weight="fill" className="w-5 h-5" /> Contact Support
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-6 sm:py-8 px-4 sm:px-6 border-t" style={{ borderColor: 'var(--border-main)' }}>
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-4">
          <div className="flex items-center gap-3">
            <Logo size="sm" />
            <span className="font-bold text-sm">index24hrs<span style={{ color: 'var(--accent)' }}>.com</span></span>
          </div>
          <p className="text-xs sm:text-sm" style={{ color: 'var(--text-muted)' }}>© 2026 index24hrs.com. Instant indexing for the modern web.</p>
        </div>
      </footer>
    </div>
  );
}
